import { Routes, Route } from 'react-router-dom';
import { Toaster, ToastBar, toast } from 'react-hot-toast';

import { ROUTES } from './constants';
import { useScrollToTop } from './hooks';

// Layout
import { Main } from './pages/Main/Main';
import { AppLayout } from './components/layout/AppLayout/AppLayout';
import { ModalBase } from './components/modals/ModalBase/ModalBase';

// Public pages
import { Home } from './pages/Home/Home';
import { NotFound } from './pages/NotFound/NotFound';

// Protected pages
import { Profile } from './pages/Profile/Profile';
import { ProtectedRoute } from './components/features/ProtectedRoute/ProtectedRoute';

// Auth pages
import { Login } from './pages/auth/Login/Login';
import { Register } from './pages/auth/Register/Register';
import { VerifyEmail } from './pages/auth/VerifyEmail/VerifyEmail';
import { ForgotPassword } from './pages/auth/ForgotPassword/ForgotPassword';
import { ChangePassword } from './pages/auth/ChangePassword/ChangePassword';
import { PendingApproval } from './pages/PendingApproval/PendingApproval';

// Dashboard pages
import { Dashboard } from './pages/Dashboard/Dashboard';
import { EmailOutreach } from './pages/EmailOutreach/EmailOutreach';
import { MailingHistory } from './pages/MailingHistory/MailingHistory';
import { WhatsappOutreach } from './pages/WhatsappOutreach/WhatsappOutreach';
import { AdminUsers } from './pages/AdminUsers/AdminUsers';
import { EmailBlacklist } from './pages/EmailBlacklist/EmailBlacklist';
import { WhatsappHistory } from './pages/WhatsappHistory/WhatsappHistory';
import { WhatsappInbox } from './pages/WhatsappInbox/WhatsappInbox';
import { MessengerInbox } from './pages/MessengerInbox/MessengerInbox';
import { InstagramInbox } from './pages/InstagramInbox/InstagramInbox';
import { SocialComments } from './pages/SocialComments/SocialComments';
import { TiktokComments } from './pages/TiktokComments/TiktokComments';
import { LeadsManage } from './pages/LeadsManage';
import { PhoneBlacklist } from './pages/PhoneBlacklist';

import './App.css';

function App() {
  // The new AuthStore no longer needs initialization as it
  // checks token validity on creation
  useScrollToTop();

  return (
    <div className="App">
      <ModalBase />
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 5000,
          success: { className: 'toast-success' },
          error: { className: 'toast-error' },
          style: {
            zIndex: 20,
          },
        }}
        children={(toasts) => (
          <ToastBar toast={toasts}>
            {({ icon, message }) => (
              <div onClick={() => toast.dismiss()} style={{ cursor: 'pointer', display: 'flex' }}>
                {icon}
                {message}
              </div>
            )}
          </ToastBar>
        )}
      />
      <Routes>
        {/* Public auth endpoints */}
        <Route path={ROUTES.login} element={<Login />} />
        <Route path={ROUTES.register} element={<Register />} />
        <Route path={ROUTES.verifyEmail} element={<VerifyEmail />} />
        <Route path={ROUTES.forgotPassword} element={<ForgotPassword />} />
        <Route path={ROUTES.pendingApproval} element={<PendingApproval />} />

        {/* Protected auth endpoints */}
        <Route element={<ProtectedRoute redirectToLogin />}>
          <Route path={ROUTES.changePassword} element={<ChangePassword />} />
        </Route>

        {/* Dashboard routes with AppLayout — require admin login */}
        <Route element={<ProtectedRoute redirectToLogin requireStaff />}>
          <Route element={<AppLayout />}>
            <Route path={ROUTES.dashboard} element={<Dashboard />} />
            <Route path={ROUTES.leadsManage} element={<LeadsManage />} />
            <Route path={ROUTES.emailOutreach} element={<EmailOutreach />} />
            <Route path={ROUTES.mailingHistory} element={<MailingHistory />} />
            <Route path={ROUTES.emailBlacklist} element={<EmailBlacklist />} />
            <Route path={ROUTES.phoneBlacklist} element={<PhoneBlacklist />} />
            <Route path={ROUTES.whatsappOutreach} element={<WhatsappOutreach />} />
            <Route path={ROUTES.whatsappHistory} element={<WhatsappHistory />} />
            <Route path={ROUTES.adminUsers} element={<AdminUsers />} />
            <Route path={ROUTES.whatsappInbox} element={<WhatsappInbox />} />
            <Route path={ROUTES.messengerInbox} element={<MessengerInbox />} />
            <Route path={ROUTES.instagramInbox} element={<InstagramInbox />} />
            <Route path={ROUTES.socialComments} element={<SocialComments />} />
            <Route path={ROUTES.tiktokComments} element={<TiktokComments />} />
          </Route>
        </Route>

        <Route path={ROUTES.home} element={<Main />}>
          {/* Public routes */}
          <Route index element={<Home />} />

          {/* Protected routes - require authentication */}
          <Route element={<ProtectedRoute />}>
            <Route path="profile" element={<Profile />} />
          </Route>
        </Route>
        <Route path={ROUTES.notFound} element={<NotFound />} />
      </Routes>
    </div>
  );
}

export { App };
